// The new search (F07) as the design «Pagina Ricerca» draws it (Figma, Versione 2,
// frames 159:2983 and 179:10824 «Plurali e Varianti»): the grey box «Cosa stai
// cercando?» with the field, the tags of the choices, «Impostazioni di ricerca» (four
// menus, «Impostazioni sulle singole parole», «Impostazioni avanzate»), «Salva ricerca» and
// «Cerca»; under it «Hai cercato:» and the results. What the box holds is a draft:
// «Cerca» (or Invio) writes it in the URL (params.ts), and the URL is the search
// (GET /search/v2, paged by offset), so the back button and a shared link give the
// same page. «Ricerche salvate» is the side panel.
import { useEffect, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { Button, Disclosure, Dropdown, Label, SearchField, Separator } from "@heroui/react";
import { useLocation, useSearchParams } from "react-router";

import { api, unwrap } from "../../api/client";

import { Icon } from "../../components/Icon";
import { NewsLayout } from "../../components/NewsLayout";
import { PanelStatus } from "../../components/PanelStatus";
import { NewsSection } from "../../components/news/NewsSection";
import { SITE_TITLE } from "../../config/site";
import { ListLoading, LoadMore } from "../news/ListStatus";
import { toSourceTree } from "../news/adapt";
import { useSources } from "../news/api";
import { useBookmarks } from "../news/bookmarks";
import {
  CHAMBER_LABELS,
  FEED_GONE,
  PERIOD_LABELS,
  RESOURCE_CHOICES,
  RESULTS_SORT_LABELS,
  SESSION_TYPE_LABELS,
  SORT_LABELS,
  WHERE_LABELS,
  WHERE_NOTES,
  WORDS_LABELS,
  WORDS_NOTES,
  choiceTags,
  whereOf,
  countOf,
  finderTermsOf,
  feedGoneOf,
  savedDraftOf,
  foundLabel,
  olderNote,
  searchFailure,
  searchedSentence,
  toSearchNewsItem,
  type SearchPage as SearchAnswer,
  type Where,
} from "./adapt";
import { usePluralForms, useSearch } from "./api";
import { ChoiceGroup } from "./ChoiceGroup";
import {
  CHAMBERS,
  DEFAULT_SEARCH_PARAMS,
  PERIODS,
  SESSION_TYPES,
  SORTS,
  WORDS,
  canSave,
  formulaOf,
  beginningsOf,
  keywordsOf,
  readSearchParams,
  toSearchQuery,
  withExcluded,
  withoutQuotes,
  writeSearchParams,
  type PagePeriod,
  type SearchParams,
} from "./params";
import { SAVED_SEARCHES_TITLE, SaveSearch, SavedSearchesPanel } from "./SavedSearchesPanel";
import { SearchCard } from "./SearchCard";
import { ChoiceTags, DayField, PreferenceSelect } from "./SearchControls";

export const SEARCH_TITLE = "Ricerca";
export const FORM_TITLE = "Cosa stai cercando?";
export const FIELD_LABEL = "Cerca nelle news e nei documenti del Parlamento";
export const START_NOTE =
  "Scrivi una o più parole e premi «Cerca». In «Impostazioni sulle singole parole» scegli, parola per parola, plurali, varianti, nome proprio o esclusione.";
/** How to write a formula: the syntax of the field, in the box «Formula» opened on demand (decisions 35, 41). */
export const HINT =
  "Tra virgolette cerchi la frase esatta («liste d’attesa»), con il meno escludi una parola (-Messina), con OR trovi l’una o l’altra (autonomia OR federalismo), con l’asterisco le parole che iniziano così (sanitar*).";
export const DEFAULTS_NOTE =
  "Se non cambi le impostazioni, verranno applicate le impostazioni di partenza (Ordina più recenti, Ultimi 6 mesi, Tutte le parole).";
export const WORDS_TITLE = "Impostazioni sulle singole parole";
export const WORDS_NOTE =
  "Clicca su ogni parola e scegli come cercarla: solo così, al singolare e al plurale, dall'inizio della parola (Legg*) o come nome proprio. Puoi anche escluderla.";
export const WORDS_NO_WORDS = "Scrivi le parole da cercare: qui sceglierai, parola per parola, come cercarle.";
export const SEARCHING = "Ricerca in corso…";
export const NO_RESULTS = "Nessun risultato per questa ricerca.";
export const MORE_FAILED = "Altri risultati non disponibili.";
export const FIRST_200 =
  "Si vedono i primi 200 risultati: aggiungi una parola o scegli un periodo più breve per trovare gli altri.";
export const SAVED_SOURCES = "Questa ricerca salvata guarda solo alcune fonti o un tuo feed.";
export const RANGE_NOT_SAVED =
  "Una ricerca con le date «Da… a…» non si salva: le ricerche salvate guardano un periodo che parte da oggi (per esempio «Ultimo mese»).";
const RESULTS_TITLE = "Risultati";

/** «Dove cercare» (decision 28): News and Parlamento, one of them, or the sources of Il mio feed. */
const WHERE_OPTIONS = (["all", "news", "parliament", "followed"] as const satisfies readonly Where[]).map((value) => ({
  value,
  label: WHERE_LABELS[value],
  note: WHERE_NOTES[value],
}));
const WHERE: Record<Where, Partial<SearchParams>> = {
  all: { origin: null, followedOnly: false },
  // the filters of «Impostazioni avanzate» are the Parlamento's: with the News only they go (Davide, 4/11)
  news: { origin: "orchestra", followedOnly: false, chamber: null, sessionType: null, resource: null },
  parliament: { origin: "coro", followedOnly: false },
  followed: { origin: null, followedOnly: true },
};

/**
 * «Formula» (decision 41: one box for «Come scrivere una formula» and «Mostra formula»):
 * how to write one, then this search written with the syntax of the field, and «Usa questa formula».
 */
function Formula({ draft, onUse }: { draft: SearchParams; onUse: (q: string) => void }) {
  // every word: its plural if chosen, its synonyms always (the formula writes them out)
  const words = keywordsOf(draft.q)
    .filter((k) => !k.common)
    .map((k) => k.key);
  const forms = useQueries({
    queries: words.map((word) => ({
      queryKey: ["search", "forms", word],
      queryFn: async () => unwrap((await api().GET("/api/v1/search/forms", { params: { query: { word } } })).data),
      staleTime: Infinity,
    })),
  });
  const known = Object.fromEntries(words.map((w, i) => [w, forms[i]?.data?.forms ?? []]));
  const synonyms = Object.fromEntries(words.map((w, i) => [w, forms[i]?.data?.synonyms ?? []]));
  const text = formulaOf(draft, known, synonyms);
  return (
    <div className="flex flex-col gap-2 rounded-lg bg-white px-[14px] py-2.5 shadow-field">
      <p aria-label="Come scrivere una formula" className="text-xs leading-5 text-muted">
        {HINT}
      </p>
      {draft.q.trim() === "" ? null : (
        <>
          <p className="text-xs leading-4 text-muted">La tua ricerca, scritta come formula:</p>
          <code aria-label="Formula della ricerca" className="font-mono text-sm leading-6 break-words text-accent">
            {text || "—"}
          </code>
          <Button
            size="sm"
            isDisabled={!text || text === draft.q.trim()}
            onPress={() => onUse(text)}
            className="h-7 w-fit gap-1 rounded-2xl bg-white px-2.5 text-xs leading-5 font-medium text-field-placeholder shadow-field"
          >
            Usa questa formula
          </Button>
        </>
      )}
    </div>
  );
}

/** The value of a multiple choice with one value toggled; null when nothing is left (no filter). */
function toggled<T extends string>(values: T[] | null, value: T): T[] | null {
  const now = values ?? [];
  const next = now.includes(value) ? now.filter((v) => v !== value) : [...now, value];
  return next.length > 0 ? next : null;
}

/** Today, YYYY-MM-DD, in the browser's time: the last day «Da… a…» may reach. */
const today = () => new Date().toLocaleDateString("sv-SE");

/** What a word of «Impostazioni sulle singole parole» is searched as: as written, with its plural, by a beginning, or as a name. */
type WordChoice = { kind: "word" } | { kind: "plural" } | { kind: "beginning"; start: string } | { kind: "name" };

const capital = (word: string) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`;

const ITEM = "rounded-[20px] px-3 py-1.5";
const ITEM_LABEL = "text-sm leading-6 font-medium text-accent";
const ITEM_NOTE = "text-xs leading-4 font-normal text-muted";
/** The name of the choice of a beginning (decision 36: «Inizia con…», a word for every consultant). */
export const BEGINNING_LABEL = "Inizia con…";
/** The choice every word starts with; it takes the others away (decision 46: «Solo «Legge»» read as one more limit). */
export const WORD_AS_TYPED = "Come l’hai scritta";
const BEGINNING_EXAMPLE = "(es. Legg* trova legge, leggi, leggere)";

/** One choice of the menu of a word, as the items of the menus of «Impostazioni di ricerca» (no check: the box says the choice). */
function WordItem({ id, text, note, isDisabled }: { id: string; text: string; note?: string; isDisabled?: boolean }) {
  return (
    <Dropdown.Item id={id} textValue={text} isDisabled={isDisabled} className={`${ITEM} data-[disabled]:opacity-50`}>
      <Label className={ITEM_LABEL}>
        {text}
        {note && <span className={ITEM_NOTE}> {note}</span>}
      </Label>
    </Dropdown.Item>
  );
}

/**
 * One word with its menu (decisions 34 and 36), the same white box and blue
 * words as the menus of «Impostazioni di ricerca»: «Come l’hai scritta», «Plurale
 * (Legge, Leggi)» (off when the archive has no other form: «Davide»), «Nome
 * proprio (Legge, LEGGE)», «Inizia con…» opening the beginnings (Legg*,
 * Legge*); apart, «Escludi questa parola», which writes the minus in the
 * field. A common word (di) can only be excluded.
 */
function WordMenu({
  word,
  excluded,
  choice,
  beginnings,
  common,
  plain,
  onChoose,
  onExclude,
  onSynonyms,
}: {
  word: string;
  excluded: boolean;
  choice: WordChoice;
  beginnings: string[];
  common: boolean;
  /** Whether the synonyms of the lexicon are taken off this word (ddl alone). */
  plain: boolean;
  onChoose: (choice: WordChoice) => void;
  onExclude: (excluded: boolean) => void;
  onSynonyms: (on: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const forms = usePluralForms(word, open && !common);
  const label = `${excluded ? "-" : ""}${capital(word)}`;
  const how0 =
    choice.kind === "plural"
      ? "singolare e plurale"
      : choice.kind === "beginning"
        ? `${capital(choice.start)}*`
        : choice.kind === "name"
          ? "nome proprio"
          : null;
  const how = how0 ?? (plain ? "senza sinonimi" : null);
  const selected = choice.kind === "beginning" ? "beginning" : choice.kind;
  const known = forms.data?.forms ?? null;
  // «Anche «disegno di legge»»: the synonyms of the lexicon, on unless taken off; none for a name
  const synonyms = choice.kind === "name" ? [] : (forms.data?.synonyms ?? []);
  const noPlural = known !== null && known.length <= 1;
  const plural =
    known === null ? undefined : noPlural ? "(nessun'altra forma nei documenti)" : `(${known.map(capital).join(", ")})`;
  return (
    <Dropdown isOpen={open} onOpenChange={setOpen}>
      <Dropdown.Trigger
        aria-label={`${label}: ${how ?? label}`}
        className={`flex h-9 cursor-pointer items-center gap-2 rounded-lg bg-white px-3 text-left shadow-field ${
          how || excluded ? "ring-1 ring-accent ring-inset" : ""
        }`}
      >
        <span className="text-sm leading-6 font-medium text-accent">{label}</span>
        {how && <span className="text-xs leading-4 text-muted">{how}</span>}
        <Icon name="chevronDown" className={`text-accent transition-transform ${open ? "-scale-y-100" : ""}`} />
      </Dropdown.Trigger>
      <Dropdown.Popover placement="bottom start" className="max-w-sm min-w-60 rounded-xl">
        <Dropdown.Menu
          aria-label={`Come cercare «${word}»`}
          onAction={(key) => {
            const k = String(key);
            if (k === "word") onChoose({ kind: "word" });
            else if (k === "plural") onChoose({ kind: "plural" });
            else if (k === "name") onChoose({ kind: "name" });
            else if (k === "synonyms") {
              setOpen(false);
              onSynonyms(plain);
            } else if (k === "exclude") {
              // a box to tick does not close the menu on its own
              setOpen(false);
              onExclude(!excluded);
            }
          }}
        >
          {common ? null : (
            <Dropdown.Section aria-label="Parola" selectionMode="single" selectedKeys={new Set([selected])}>
              <WordItem id="word" text={WORD_AS_TYPED} note="(nessuna scelta su questa parola)" />
              <WordItem
                id="plural"
                text="Singolare e plurale"
                note={plural}
                isDisabled={noPlural && choice.kind !== "plural"}
              />
              <WordItem id="name" text="Nome proprio" note={`(${capital(word)}, ${word.toUpperCase()})`} />
              {beginnings.length > 0 ? (
                <Dropdown.SubmenuTrigger>
                  <Dropdown.Item id="beginning" textValue={BEGINNING_LABEL} className={ITEM}>
                    <Label className={ITEM_LABEL}>
                      {BEGINNING_LABEL}
                      <span className={ITEM_NOTE}>
                        {" "}
                        {choice.kind === "beginning" ? `${capital(choice.start)}*` : BEGINNING_EXAMPLE}
                      </span>
                    </Label>
                    <Dropdown.SubmenuIndicator />
                  </Dropdown.Item>
                  <Dropdown.Popover className="min-w-40 rounded-xl">
                    <Dropdown.Menu
                      aria-label={`${BEGINNING_LABEL} «${word}»`}
                      selectionMode="single"
                      selectedKeys={new Set(choice.kind === "beginning" ? [`start:${choice.start}`] : [])}
                      onAction={(key) => {
                        setOpen(false);
                        onChoose({ kind: "beginning", start: String(key).slice("start:".length) });
                      }}
                    >
                      {beginnings.map((start) => (
                        <WordItem key={start} id={`start:${start}`} text={`${capital(start)}*`} />
                      ))}
                    </Dropdown.Menu>
                  </Dropdown.Popover>
                </Dropdown.SubmenuTrigger>
              ) : null}
            </Dropdown.Section>
          )}
          {synonyms.length > 0 ? (
            <Dropdown.Section
              aria-label="Sinonimi"
              selectionMode="multiple"
              selectedKeys={new Set(plain ? [] : ["synonyms"])}
            >
              <WordItem
                id="synonyms"
                text={`Anche ${synonyms.map((x) => `«${x}»`).join(", ")}`}
                note="(sinonimo dal lessico di PolitorAI)"
              />
            </Dropdown.Section>
          ) : null}
          <Dropdown.Section
            aria-label="Escludi"
            selectionMode="multiple"
            selectedKeys={new Set(excluded ? ["exclude"] : [])}
          >
            <WordItem id="exclude" text="Escludi questa parola" note="(i documenti che la contengono non escono)" />
          </Dropdown.Section>
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  );
}

/** «Impostazioni sulle singole parole»: one tag per word of the search, each with its menu (decision 34). */
function WordsPanel({ draft, onChange }: { draft: SearchParams; onChange: (next: Partial<SearchParams>) => void }) {
  const keywords = keywordsOf(draft.q);
  // a beginning is for a word on its own: not in «Frase esatta»
  const phrase = draft.words === "phrase";
  const choose = (key: string, choice: WordChoice) => {
    // a word in quotes is «Solo» that word: another choice takes the quotes away
    const quoted = keywords.find((k) => k.key === key)?.quoted ?? false;
    const q = quoted && choice.kind !== "word" ? withoutQuotes(draft.q, key) : draft.q;
    const variantsFor = draft.variantsFor.filter((k) => k !== key);
    const namesFor = draft.namesFor.filter((k) => k !== key);
    const prefixFor = { ...draft.prefixFor };
    delete prefixFor[key];
    if (choice.kind === "plural") variantsFor.push(key);
    if (choice.kind === "name") namesFor.push(key);
    if (choice.kind === "beginning") prefixFor[key] = choice.start;
    onChange({ q, variantsFor, prefixFor, namesFor });
  };
  return (
    <div className="flex flex-col gap-2.5">
      <p className="text-xs leading-4 text-muted">{WORDS_NOTE}</p>
      {keywords.length === 0 ? (
        <p className="text-xs leading-4 text-muted">{WORDS_NO_WORDS}</p>
      ) : (
        <ul aria-label={WORDS_TITLE} className="flex flex-wrap items-center gap-2.5">
          {keywords.map(({ word, key, excluded, common, quoted }) => {
            const start = draft.prefixFor[key];
            const choice: WordChoice = common
              ? { kind: "word" }
              : quoted
                ? { kind: "word" }
                : draft.namesFor.includes(key)
                  ? { kind: "name" }
                  : start && !phrase
                    ? { kind: "beginning", start }
                    : draft.variantsFor.includes(key)
                      ? { kind: "plural" }
                      : { kind: "word" };
            return (
              <li key={key}>
                <WordMenu
                  word={word}
                  excluded={excluded}
                  choice={choice}
                  beginnings={phrase ? [] : beginningsOf(key)}
                  common={common}
                  plain={draft.noSynonymsFor.includes(key)}
                  onSynonyms={(on) =>
                    onChange({
                      noSynonymsFor: on ? draft.noSynonymsFor.filter((k) => k !== key) : [...draft.noSynonymsFor, key],
                    })
                  }
                  onChoose={(next) => choose(key, next)}
                  onExclude={(on) => onChange({ q: withExcluded(draft.q, key, on) })}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** A part of «Impostazioni di ricerca» that opens under its title: «Impostazioni sulle singole parole», «Impostazioni avanzate». */
function Expandable({
  title,
  open,
  onOpenChange,
  children,
}: {
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <Disclosure isExpanded={open} onExpandedChange={onOpenChange} className="flex flex-col">
      <Disclosure.Heading>
        <Disclosure.Trigger className="flex items-center gap-2.5 rounded-sm text-left text-sm leading-6 font-medium text-black">
          {title}
          <Icon name="chevronDown" className={`transition-transform ${open ? "-scale-y-100" : ""}`} />
        </Disclosure.Trigger>
      </Disclosure.Heading>
      <Disclosure.Content>
        <div className="pt-2.5">{children}</div>
      </Disclosure.Content>
    </Disclosure>
  );
}

/** «Impostazioni avanzate»: the filters of the Parlamento, closed at first. */
function Advanced({ draft, onChange }: { draft: SearchParams; onChange: (next: Partial<SearchParams>) => void }) {
  const active = draft.chamber !== null || draft.sessionType !== null || draft.resource !== null;
  const [open, setOpen] = useState(active);
  return (
    <Expandable title="Impostazioni avanzate" open={open} onOpenChange={setOpen}>
      <div className="flex flex-col gap-[15px]">
        <ChoiceGroup
          label="Camera o Senato"
          choices={CHAMBERS.map((value) => ({ value, label: CHAMBER_LABELS[value] }))}
          isSelected={(value) => draft.chamber?.includes(value) ?? false}
          onPress={(value) => onChange({ chamber: toggled(draft.chamber, value) })}
        />
        <ChoiceGroup
          label="Plenaria o Commissioni"
          choices={SESSION_TYPES.map((value) => ({ value, label: SESSION_TYPE_LABELS[value] }))}
          isSelected={(value) => draft.sessionType?.includes(value) ?? false}
          onPress={(value) => onChange({ sessionType: toggled(draft.sessionType, value) })}
        />
        <ChoiceGroup
          label="Tipo di documento"
          choices={RESOURCE_CHOICES.map(({ label }) => ({ value: label, label }))}
          isSelected={(label) => {
            const values = RESOURCE_CHOICES.find((c) => c.label === label)?.values ?? [];
            return values.length > 0 && values.every((v) => draft.resource?.includes(v));
          }}
          onPress={(label) => {
            const values = RESOURCE_CHOICES.find((c) => c.label === label)?.values ?? [];
            const now = draft.resource ?? [];
            const on = values.every((v) => now.includes(v));
            const next = on ? now.filter((v) => !values.includes(v)) : [...new Set([...now, ...values])];
            onChange({ resource: next.length > 0 ? next : null });
          }}
        />
      </div>
    </Expandable>
  );
}

type SearchFormProps = {
  initial: SearchParams;
  onSearch: (params: SearchParams) => void;
  saving: boolean;
  onSaving: (open: boolean) => void;
  notes: React.ReactNode;
  /** «Pulisci tutto»: back to the empty page, without loading it again. */
  onClear: () => void;
};

/** The grey box «Cosa stai cercando?»: a draft of the search until «Cerca». */
function SearchForm({ initial, onSearch, saving, onSaving, notes, onClear }: SearchFormProps) {
  const [draft, setDraft] = useState<SearchParams>(initial);
  const [plurals, setPlurals] = useState(
    initial.variantsFor.length > 0 || Object.keys(initial.prefixFor).length > 0 || initial.namesFor.length > 0,
  );
  const [formula, setFormula] = useState(false);
  const change = (next: Partial<SearchParams>) => setDraft((d) => ({ ...d, ...next }));
  const period: PagePeriod = draft.period;
  const submit = () => {
    // «Da… a…» without both days is not a period yet: the default one
    const ready = draft.period !== "range" || (draft.dateFrom && draft.dateTo && draft.dateFrom <= draft.dateTo);
    onSearch(ready ? draft : { ...draft, period: DEFAULT_SEARCH_PARAMS.period, dateFrom: null, dateTo: null });
  };

  return (
    // The grey band runs edge to edge of the white page (its shadow, cut by the page's overflow-x-clip),
    // the words inside stay in line with the results.
    <section
      aria-labelledby="ricerca-titolo"
      className="-mt-2 flex flex-col items-stretch gap-[26px] bg-slate-200 pt-[18px] pb-[18px] shadow-[0_0_0_100vmax_var(--color-slate-200)] [clip-path:inset(0_-100vmax)]"
    >
      <h1 id="ricerca-titolo" className="text-center text-xl leading-7 font-semibold text-brand">
        {FORM_TITLE}
      </h1>
      <form
        role="search"
        aria-label={SEARCH_TITLE}
        className="flex flex-col gap-[26px]"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="flex flex-col gap-2.5">
          <SearchField value={draft.q} onChange={(q) => change({ q })} className="flex w-full flex-col">
            <Label className="sr-only">{FIELD_LABEL}</Label>
            <SearchField.Group className="h-9 rounded-xl bg-field-background px-3 shadow-field">
              <SearchField.SearchIcon>
                <Icon name="magnifier" />
              </SearchField.SearchIcon>
              <SearchField.Input placeholder="Cerca…" className="text-sm leading-5" />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>
          <ChoiceTags
            tags={choiceTags(draft)}
            onReset={change}
            formulaOpen={formula}
            onFormula={() => setFormula((o) => !o)}
          />
          {formula && (
            <Formula
              draft={draft}
              onUse={(q) =>
                change({
                  q,
                  words: "all",
                  // a name stays the choice of its word: the formula has no syntax for it (decision 29)
                  mode: "synonyms",
                  variantsFor: [],
                  prefixFor: {},
                  // the formula writes the synonyms out, or the word in quotes without them
                  noSynonymsFor: [],
                })
              }
            />
          )}
        </div>

        <div className="flex flex-col gap-2.5 rounded-lg bg-white px-[14px] py-[13px] shadow-field">
          <div className="flex flex-col gap-2">
            <h2 className="text-sm leading-6 font-medium text-black">Impostazioni di ricerca</h2>
            <p className="text-xs leading-4 text-muted">{DEFAULTS_NOTE}</p>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <PreferenceSelect
              label="Dove cercare"
              options={WHERE_OPTIONS}
              value={whereOf(draft)}
              onChange={(v) => change(WHERE[v])}
            />
            <PreferenceSelect
              label="Periodo"
              options={[
                ...PERIODS.map((value) => ({ value, label: PERIOD_LABELS[value] })),
                { value: "range" as const, label: "Da… a…" },
              ]}
              value={period}
              onChange={(v) => change(v === "range" ? { period: v } : { period: v, dateFrom: null, dateTo: null })}
            />
            <PreferenceSelect
              label="Parole"
              options={WORDS.map((value) => ({ value, label: WORDS_LABELS[value], note: WORDS_NOTES[value] }))}
              value={draft.words}
              onChange={(words) => change({ words })}
            />
          </div>
          {period === "range" && (
            <div className="flex flex-wrap gap-2.5">
              <DayField
                label="Dal"
                value={draft.dateFrom}
                max={draft.dateTo ?? today()}
                onChange={(dateFrom) => change({ dateFrom })}
              />
              <DayField
                label="Al"
                value={draft.dateTo}
                min={draft.dateFrom ?? undefined}
                max={today()}
                onChange={(dateTo) => change({ dateTo })}
              />
            </div>
          )}
          <div className="flex flex-col gap-5 pt-6">
            <Expandable title={WORDS_TITLE} open={plurals} onOpenChange={setPlurals}>
              <WordsPanel draft={draft} onChange={change} />
            </Expandable>
            {whereOf(draft) !== "news" && <Advanced draft={draft} onChange={change} />}
          </div>
        </div>

        {notes}

        <div className="flex flex-wrap items-center justify-end gap-2.5">
          <Button
            size="sm"
            isDisabled={JSON.stringify(draft) === JSON.stringify(DEFAULT_SEARCH_PARAMS) && !formula && !plurals}
            onPress={() => {
              setDraft(DEFAULT_SEARCH_PARAMS);
              setFormula(false);
              setPlurals(false);
              onClear();
            }}
            className="mr-auto h-8 gap-1 rounded-2xl bg-white px-3 text-sm leading-5 font-medium text-accent-soft-foreground shadow-field"
          >
            <Icon name="xmark" />
            Pulisci tutto
          </Button>
          <Button
            size="sm"
            isDisabled={!canSave(draft)}
            onPress={() => onSaving(!saving)}
            className="h-8 gap-1 rounded-2xl bg-white px-3 text-sm leading-5 font-medium text-accent-soft-foreground shadow-field"
          >
            <Icon name="folderPlus" />
            Salva ricerca
          </Button>
          <Button
            type="submit"
            size="sm"
            className="h-8 gap-1 rounded-2xl bg-accent px-3 text-sm leading-5 font-medium text-accent-foreground"
          >
            <Icon name="paperPlane" />
            Cerca
          </Button>
        </div>
        {draft.period === "range" && draft.q.trim() !== "" && (
          <PanelStatus role="note" indent={false}>
            {RANGE_NOT_SAVED}
          </PanelStatus>
        )}
        {saving && canSave(draft) && <SaveSearch params={draft} onDone={() => onSaving(false)} />}
      </form>
    </section>
  );
}

type SearchPageProps = {
  /** What stands in for the page when the api says the switch is off: the page of today. */
  off: React.ReactNode;
};

export function SearchPage({ off }: SearchPageProps) {
  const [search, setSearch] = useSearchParams();
  // reopened from Ricerche salvate without its deleted feed: said until the user changes the search
  const location = useLocation();
  const feedGone = feedGoneOf(location.state);
  const params = useMemo(() => readSearchParams(search), [search]);
  // a saved search opened from the panel: in the form, not sent until «Cerca» (decision 44)
  const savedDraft = useMemo(() => {
    const draft = savedDraftOf(location.state);
    return draft ? readSearchParams(draft) : null;
  }, [location.state]);
  const query = useMemo(() => toSearchQuery(params), [params]);
  const hasWords = params.q !== "";
  const results = useSearch(query, hasWords);
  const catalogue = useSources();
  const tree = useMemo(() => (catalogue.data ? toSourceTree(catalogue.data) : null), [catalogue.data]);
  const bookmarks = useBookmarks();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const before = document.title;
    document.title = `${SEARCH_TITLE} · ${SITE_TITLE}`;
    return () => {
      document.title = before;
    };
  }, []);

  const first: SearchAnswer | undefined = results.data?.pages[0];
  const finder = useMemo(() => (first ? finderTermsOf(first.searched) : []), [first]);
  const items = useMemo(
    () => (results.data?.pages.flatMap((page) => page.items) ?? []).map((hit) => toSearchNewsItem(hit, tree, finder)),
    [results.data, tree, finder],
  );

  // a failed search being retried is loading again, not still failed
  const failed = results.isError && !results.isFetching && results.data === undefined;
  const failure = failed ? searchFailure(results.error) : null;
  if (failure?.kind === "off") return <>{off}</>;

  const notes = (
    <>
      {!hasWords && (
        <PanelStatus role="note" indent={false}>
          {START_NOTE}
        </PanelStatus>
      )}
      {feedGone && (
        <PanelStatus role="note" indent={false}>
          {FEED_GONE}
        </PanelStatus>
      )}
      {(params.sources !== null || params.feedId !== null) && (
        <PanelStatus role="note" indent={false}>
          {SAVED_SOURCES}
        </PanelStatus>
      )}
    </>
  );

  let status: React.ReactNode = null;
  if (failure?.kind === "message") {
    status = (
      <PanelStatus role="alert" indent={false} onRetry={failure.retry ? () => void results.refetch() : undefined}>
        {failure.message}
      </PanelStatus>
    );
  } else if (hasWords && results.data === undefined) {
    status = <ListLoading label={SEARCHING} />;
  } else if (hasWords) {
    const rows: React.ReactNode[] = [];
    const loaded = items.length;
    const total = first ? countOf(first, params.origin) : null;
    if (loaded === 0)
      rows.push(
        <PanelStatus key="empty" indent={false}>
          {NO_RESULTS}
        </PanelStatus>,
      );
    if (results.isFetchNextPageError) {
      rows.push(
        <PanelStatus key="more-failed" role="alert" indent={false} onRetry={() => void results.fetchNextPage()}>
          {MORE_FAILED}
        </PanelStatus>,
      );
    } else if (results.hasNextPage) {
      rows.push(
        <LoadMore
          key="more"
          label="Carica altri"
          isDisabled={results.isFetchingNextPage}
          onPress={() => void results.fetchNextPage()}
        />,
      );
    } else if (total !== null && loaded >= 200 && (total.capped || total.count > loaded)) {
      rows.push(
        <PanelStatus key="first-200" role="note" indent={false}>
          {FIRST_200}
        </PanelStatus>,
      );
    }
    if (first?.older_available && params.period !== "all" && params.period !== "range") {
      rows.push(
        <PanelStatus
          key="older"
          role="note"
          indent={false}
          retryLabel="Cerca in tutto l’archivio"
          onRetry={() => setSearch(writeSearchParams({ ...params, period: "all" }))}
        >
          {olderNote(params.period)}
        </PanelStatus>,
      );
    }
    status = <>{rows}</>;
  }

  const header = (
    <header className="flex flex-col gap-[17px]">
      {first && (
        <div className="flex flex-col gap-2">
          <h2 className="text-sm leading-6 font-medium text-black">Hai cercato:</h2>
          <p className="text-xs leading-4 text-muted">{searchedSentence(first.searched)}</p>
        </div>
      )}
      <Separator variant="secondary" />
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl leading-7 font-semibold text-brand">{RESULTS_TITLE}</h2>
          {first && (
            <p className="text-xs leading-5 font-medium text-muted">{foundLabel(countOf(first, params.origin))}</p>
          )}
        </div>
        <div className="flex items-center gap-2.5">
          <Dropdown>
            <Dropdown.Trigger
              aria-label={`Ordine dei risultati: ${SORT_LABELS[params.sort]}`}
              className="flex h-8 cursor-pointer items-center gap-1 rounded-2xl bg-white px-3 text-sm leading-5 font-medium text-accent-soft-foreground shadow-field"
            >
              {RESULTS_SORT_LABELS[params.sort]}
              <Icon name="chevronDown" />
            </Dropdown.Trigger>
            <Dropdown.Popover placement="bottom end" className="min-w-56 rounded-xl">
              <Dropdown.Menu
                aria-label="Ordine dei risultati"
                selectionMode="single"
                selectedKeys={new Set([params.sort])}
                onAction={(key) => {
                  const sort = SORTS.find((s) => s === key);
                  if (sort) setSearch(writeSearchParams({ ...params, sort }));
                }}
              >
                {SORTS.map((value) => (
                  <Dropdown.Item
                    key={value}
                    id={value}
                    textValue={RESULTS_SORT_LABELS[value]}
                    className="rounded-[20px] px-3 py-1.5"
                  >
                    <Label className="text-sm leading-6 font-medium text-accent">{RESULTS_SORT_LABELS[value]}</Label>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
          <Button
            isIconOnly
            size="sm"
            aria-label="Salva ricerca"
            isDisabled={!canSave(params)}
            onPress={() => {
              setSaving(true);
              document.getElementById("ricerca-titolo")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="size-8 min-w-8 rounded-2xl bg-white shadow-field"
          >
            <Icon name="folderPlus" />
          </Button>
        </div>
      </div>
    </header>
  );

  return (
    <NewsLayout
      sidebar={{ title: SAVED_SEARCHES_TITLE, render: (collapse) => <SavedSearchesPanel onCollapse={collapse} /> }}
      labelledBy="ricerca-titolo"
    >
      <SearchForm
        key={`${search.toString()}|${location.key}`}
        initial={savedDraft ?? params}
        onSearch={(next) => {
          setSaving(false);
          setSearch(writeSearchParams(next));
        }}
        saving={saving}
        onSaving={setSaving}
        notes={notes}
        onClear={() => {
          setSaving(false);
          setSearch(new URLSearchParams());
        }}
      />
      {hasWords && (
        <NewsSection
          title={RESULTS_TITLE}
          header={header}
          items={items}
          onToggleSaved={bookmarks.toggle}
          card={SearchCard}
        >
          {bookmarks.failure && (
            <PanelStatus role="alert" indent={false} onRetry={bookmarks.failure.onRetry}>
              {bookmarks.failure.message}
            </PanelStatus>
          )}
          {status}
        </NewsSection>
      )}
    </NewsLayout>
  );
}
